import React, { useEffect, useRef, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    AreaChart,
    Area,
    ComposedChart
} from "recharts";
import { getUserActivityAnalytics } from "../../api/context/analytics";
import { FiActivity, FiUsers, FiClock, FiShoppingCart, FiEye, FiDollarSign, FiRefreshCw, FiStore, FiTag, FiGrid, FiPackage, FiSearch, FiList, FiAlertTriangle, FiMapPin, FiGift, FiChevronRight, FiCalendar, FiX } from "react-icons/fi";
import Loader from '../../components/loader/loader';
import toast from "react-hot-toast";
import { FaStore, FaWandMagicSparkles } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

const COLORS = ["#6366F1", "#22C55E", "#FACC15", "#F97316", "#EC4899", "#06B6D4", "#8B5CF6", "#F43F5E"];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDdMmYy = (iso) => {
    if (!iso || typeof iso !== 'string' || iso.length < 10) return '';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return '';
    const monthIdx = Number(m) - 1;
    const monthName = MONTH_NAMES[monthIdx] || m;
    return `${Number(d)} ${monthName} ${y}`;
};

const AnalyticsDashboard = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());
    const [presetOpen, setPresetOpen] = useState(false);
    const presetWrapRef = useRef(null);
    const [showDateSheet, setShowDateSheet] = useState(false);
    const [sheetStart, setSheetStart] = useState(filters.startDate);
    const [sheetEnd, setSheetEnd] = useState(filters.endDate);

    // Shared preset definitions (used by both mobile bottom sheet + desktop dropdown)
    const HOMEPAGE_PRESETS = [
        { key: 'today',       label: 'Today',         days: 0 },
        { key: 'yesterday',   label: 'Yesterday',     days: 1, one: true },
        { key: 'last7',       label: 'Last 7 days',   days: 6 },
        { key: 'last15',      label: 'Last 15 days',  days: 14 },
        { key: 'last30',      label: 'Last 30 days',  days: 29 },
        { key: 'thisMonth',   label: 'This month',    thisMonth: true },
        { key: 'lastMonth',   label: 'Last month',    lastMonth: true },
        { key: 'last2m',      label: 'Last 2 months', months: 2 },
        { key: 'last3m',      label: 'Last 3 months', months: 3 },
        { key: 'last6m',      label: 'Last 6 months', months: 6 },
        { key: 'thisYear',    label: 'This year',     thisYear: true },
    ];
    const computePresetRange = (p) => {
        const today = new Date();
        let s, e;
        if (p.thisMonth) {
            s = new Date(today.getFullYear(), today.getMonth(), 1);
            e = today;
        } else if (p.lastMonth) {
            s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            e = new Date(today.getFullYear(), today.getMonth(), 0);
        } else if (p.thisYear) {
            s = new Date(today.getFullYear(), 0, 1);
            e = today;
        } else if (p.months) {
            s = new Date(today);
            s.setMonth(s.getMonth() - p.months);
            e = today;
        } else if (p.one) {
            s = new Date(today);
            s.setDate(s.getDate() - p.days);
            e = new Date(s);
        } else {
            s = new Date(today);
            s.setDate(s.getDate() - p.days);
            e = today;
        }
        const fmt = (d) => d.toISOString().split('T')[0];
        return { start: fmt(s), end: fmt(e) };
    };

    const openDateSheet = () => {
        setSheetStart(filters.startDate);
        setSheetEnd(filters.endDate);
        setShowDateSheet(true);
    };
    const applyDateSheet = () => {
        if (!sheetStart || !sheetEnd) {
            toast.error('Please select both start and end dates');
            return;
        }
        if (sheetStart > sheetEnd) {
            toast.error('Start date cannot be after end date');
            return;
        }
        setFilters({ startDate: sheetStart, endDate: sheetEnd });
        setShowDateSheet(false);
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const formatActivityType = (type) => {
        const types = {
            'cart_add': 'Cart Adds',
            'store_view': 'Store Views',
            'purchase': 'Purchases',
            'product_view': 'Product Views',
            'brand_view': 'Brand Views',
            'category_view': 'Category Views',
            'search': 'Searches'
        };
        return types[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const formatTimeSpent = (minutes) => {
        if (minutes < 60) {
            return `${Math.round(minutes)} min`;
        }
        const hours = (minutes / 60).toFixed(1);
        return `${hours} hrs`;
    };

    const fetchAnalytics = async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        const requestBody = {
            startDate: filters.startDate,
            endDate: filters.endDate
        };

        console.log("📊 User Activity params:", requestBody);

        try {
            await new Promise((resolve, reject) => {
                getUserActivityAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);

                        const start = new Date(filters.startDate);
                        const end = new Date(filters.endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

                        toast.success(`Activity data fetched for ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`);
                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject(new Error('Failed to fetch activity analytics'));
                    }
                });
            });
        } catch (error) {
            console.error("Error fetching activity analytics:", error);
            setAnalytics(null);
            toast.error('Failed to fetch activity data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    useEffect(() => {
        if (!presetOpen) return;
        const handleDocClick = (e) => {
            if (presetWrapRef.current && !presetWrapRef.current.contains(e.target)) {
                setPresetOpen(false);
            }
        };
        const handleEsc = (e) => { if (e.key === 'Escape') setPresetOpen(false); };
        document.addEventListener('mousedown', handleDocClick);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleDocClick);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [presetOpen]);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();

        if (key === 'startDate' && value > today) {
            toast.error('Start date cannot be in the future');
            return;
        }

        if (key === 'endDate' && value > today) {
            toast.error('End date cannot be in the future');
            return;
        }

        if (key === 'startDate' && filters.endDate && value > filters.endDate) {
            toast.error('Start date cannot be after end date');
            return;
        }

        if (key === 'endDate' && filters.startDate && value < filters.startDate) {
            toast.error('End date cannot be before start date');
            return;
        }

        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    useEffect(() => {
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());

        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => {
                fetchAnalytics(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [filters]);

    const handleClearFilters = () => {
        const defaultDates = getDefaultDates();
        setFilters(defaultDates);

        setTimeout(() => {
            setRefreshing(true);
            const requestBody = {
                startDate: defaultDates.startDate,
                endDate: defaultDates.endDate
            };

            getUserActivityAnalytics(requestBody)((response) => {
                if (response?.statusCode === 200) {
                    setAnalytics(response?.data);
                } else {
                    setAnalytics(null);
                    toast.error('Failed to fetch activity data');
                }
                setRefreshing(false);
            });
        }, 100);
    };

    const isFilterActive = () => {
        const defaultDates = getDefaultDates();
        return filters.startDate !== defaultDates.startDate || filters.endDate !== defaultDates.endDate;
    };

    // Helper function to get activity data for specific types
    const getActivityData = (type) => {
        if (!analytics?.activityBreakdown) return null;
        return analytics.activityBreakdown.find(activity => activity.activityType === type);
    };

    const isLoadingInitial = loading && !refreshing;
    const hasNoData = !loading && !analytics;

    return (
        <div className="min-vh-100 analytics-dashboard-page">
            <style>{`
                /* ===== Base page tokens (mirrors homepage) ===== */
                .analytics-dashboard-page { color: #0f172a; background: #f9fafb; min-height: calc(100vh - 70px); }
                .main-content:has(.analytics-dashboard-page) { background: #f9fafb; }
                .page-content:has(.analytics-dashboard-page) { background: #f9fafb; }
                .analytics-dashboard-page h3 { font-size: 18px !important; }
                .analytics-dashboard-page h4 { font-size: 20px !important; }
                .analytics-dashboard-page h5 { font-size: 14px !important; }
                .analytics-dashboard-page h6 { font-size: 12px !important; }
                .analytics-dashboard-page p { font-size: 12px; }
                .analytics-dashboard-page .small,
                .analytics-dashboard-page small { font-size: 11px !important; }
                .analytics-dashboard-page .badge { font-size: 10px !important; padding: 3px 7px !important; }
                .analytics-dashboard-page .btn { font-size: 12px; }
                .analytics-dashboard-page .recharts-text { font-size: 10px !important; }

                /* Uniform vertical rhythm between sections */
                .analytics-dashboard-page .analytics-section { margin-bottom: 16px; }
                .analytics-dashboard-page .analytics-section:last-child { margin-bottom: 0; }

                /* ===== Modern panel (mirrors homepage) ===== */
                .analytics-dashboard-page .modern-panel {
                    background: #fff;
                    border: 1px solid #eef2f7;
                    border-radius: 16px;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03);
                    overflow: hidden;
                }
                .analytics-dashboard-page .modern-panel-header {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: clamp(12px, 1.2vw, 18px) clamp(14px, 1.4vw, 20px) clamp(8px, 0.9vw, 12px);
                    gap: 12px; flex-wrap: wrap;
                }
                .analytics-dashboard-page .modern-panel-body {
                    padding: 8px clamp(14px, 1.4vw, 20px) clamp(14px, 1.4vw, 20px);
                }
                .analytics-dashboard-page .panel-title {
                    font-size: 15px; font-weight: 700; color: #0f172a; margin: 0;
                    letter-spacing: -0.01em;
                }
                .analytics-dashboard-page .panel-subtle {
                    font-size: 11px; color: #94a3b8; font-weight: 500; margin: 2px 0 0;
                }
                .analytics-dashboard-page .panel-count {
                    font-size: 10px; font-weight: 600; color: #64748b;
                    padding: 4px 10px; border-radius: 999px;
                    background: #f1f5f9; letter-spacing: 0.02em;
                }

                /* ===== Modern KPI card (mirrors homepage stat card) ===== */
                .analytics-dashboard-page .modern-stat-card {
                    background: #ffffff;
                    border: 1px solid #f1f5f9;
                    border-radius: 16px;
                    padding: clamp(14px, 1.4vw, 20px);
                    transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.15s ease;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03);
                    display: flex;
                    flex-direction: column;
                    cursor: pointer;
                }
                .analytics-dashboard-page .modern-stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 28px -18px rgba(15,23,42,0.14);
                    border-color: #e2e8f0;
                }
                .analytics-dashboard-page .stat-top-row {
                    display: flex; align-items: center; justify-content: space-between;
                    margin-bottom: 14px; gap: 10px;
                }
                .analytics-dashboard-page .stat-label {
                    font-size: 12px; font-weight: 500; color: #64748b; margin: 0;
                }
                .analytics-dashboard-page .stat-icon-wrap {
                    width: 34px; height: 34px;
                    border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .analytics-dashboard-page .stat-value {
                    font-size: 20px; font-weight: 700; color: #0f172a;
                    line-height: 1.1; letter-spacing: -0.02em; margin: 0;
                }
                .analytics-dashboard-page .stat-foot {
                    margin-top: 10px; padding-top: 8px;
                    border-top: 1px solid #f1f5f9;
                    display: flex; align-items: center; gap: 12px;
                }
                .analytics-dashboard-page .stat-foot-item {
                    display: inline-flex; align-items: center; gap: 4px;
                    font-size: 10.5px; color: #94a3b8; font-weight: 500;
                }

                /* ===== Empty state (mirrors homepage live-orders-empty) ===== */
                .analytics-dashboard-page .analytics-empty {
                    width: 100%;
                    min-height: 320px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 32px 20px;
                    text-align: center;
                    color: #64748b;
                    background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
                    border-radius: 12px;
                    border: 1px solid #eef2f7;
                }
                .analytics-dashboard-page .analytics-empty-icon {
                    font-size: 40px; margin-bottom: 12px;
                    opacity: 0.7;
                }
                .analytics-dashboard-page .analytics-empty-title {
                    font-size: 15px; font-weight: 700; color: #0f172a;
                    letter-spacing: -0.01em; margin-bottom: 4px;
                }
                .analytics-dashboard-page .analytics-empty-sub {
                    font-size: 12px; color: #94a3b8; font-weight: 500;
                }

                /* ===== Horizontal card strip (Activity Overview + Top sections) ===== */
                .analytics-dashboard-page .h-card-strip {
                    display: flex;
                    gap: 12px;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                    scroll-snap-type: x proximity;
                    padding-bottom: 4px;
                }
                .analytics-dashboard-page .h-card-strip::-webkit-scrollbar { display: none; }
                .analytics-dashboard-page .h-card-strip > * { scroll-snap-align: start; flex-shrink: 0; }

                /* ===== Top-performer card ===== */
                .analytics-dashboard-page .top-card {
                    background: #fff;
                    border: 1px solid #f1f5f9;
                    border-radius: 12px;
                    padding: 12px;
                    min-width: 240px;
                    display: flex;
                    gap: 12px;
                    align-items: flex-start;
                    transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.2s ease;
                }
                .analytics-dashboard-page .top-card:hover {
                    transform: translateY(-2px);
                    border-color: #e2e8f0;
                    box-shadow: 0 10px 24px -18px rgba(15,23,42,0.14);
                }
                .analytics-dashboard-page .top-card-img-wrap { position: relative; flex-shrink: 0; }
                .analytics-dashboard-page .top-card-img,
                .analytics-dashboard-page .top-card-fallback {
                    width: 68px; height: 68px;
                    border-radius: 10px; object-fit: cover;
                    background: #f8fafc;
                    display: flex; align-items: center; justify-content: center;
                    color: #94a3b8;
                }
                .analytics-dashboard-page .top-card-rank {
                    position: absolute;
                    top: -6px; left: -6px;
                    background: #fff;
                    color: #0f172a;
                    font-size: 10px;
                    font-weight: 700;
                    padding: 2px 8px;
                    border-radius: 999px;
                    box-shadow: 0 2px 6px rgba(15,23,42,0.08);
                    z-index: 1;
                }
                .analytics-dashboard-page .top-card-name {
                    font-size: 12px; font-weight: 600; color: #0f172a;
                    margin: 0 0 4px;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                    max-width: 150px;
                }
                .analytics-dashboard-page .top-card-value {
                    font-size: 16px; font-weight: 700; color: #0f172a;
                    letter-spacing: -0.02em; margin: 0 0 6px;
                }
                .analytics-dashboard-page .top-card-meta {
                    display: flex; align-items: center; gap: 10px;
                    font-size: 10.5px; color: #94a3b8; font-weight: 500;
                }
                .analytics-dashboard-page .top-card-meta-item {
                    display: inline-flex; align-items: center; gap: 4px;
                }

                /* ===== Quick access card (accent-tinted) ===== */
                .analytics-dashboard-page .qa-card {
                    background: #fff;
                    border: 1px solid #f1f5f9;
                    border-radius: 12px;
                    padding: 12px 14px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    cursor: pointer;
                    transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.15s ease;
                }
                .analytics-dashboard-page .qa-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 24px -18px rgba(15,23,42,0.14);
                    border-color: #e2e8f0;
                }
                .analytics-dashboard-page .qa-icon {
                    width: 36px; height: 36px;
                    border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .analytics-dashboard-page .qa-title {
                    font-size: 12px; font-weight: 700; color: #0f172a;
                    margin: 0; letter-spacing: -0.01em;
                }
                .analytics-dashboard-page .qa-sub {
                    font-size: 10.5px; color: #94a3b8; font-weight: 500;
                    margin: 2px 0 0;
                }
                .analytics-dashboard-page .qa-badge {
                    background: #f5f3ff;
                    color: #7c3aed;
                    font-weight: 700;
                    font-size: 9px !important;
                    letter-spacing: 0.06em;
                    padding: 3px 7px !important;
                    border-radius: 999px;
                }

                /* Header layout (matches homepage dashboard) */
                .analytics-dashboard-page .analytics-header {
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                }
                .analytics-dashboard-page .analytics-greeting { min-width: 0; }
                .analytics-dashboard-page .analytics-greeting-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                }
                .analytics-dashboard-page .analytics-greeting-text { min-width: 0; }
                .analytics-dashboard-page .analytics-greeting-title {
                    font-size: 18px;
                    line-height: 1.25;
                    letter-spacing: -0.01em;
                    color: #111827;
                    margin: 0;
                }
                .analytics-dashboard-page .analytics-greeting-sub {
                    font-size: 12px;
                    color: #94a3b8 !important;
                    font-weight: 500;
                    margin: 2px 0 0 !important;
                    line-height: 1.35;
                }
                @media (max-width: 767.98px) {
                    .analytics-dashboard-page .analytics-greeting-title { font-size: 14px !important; }
                    .analytics-dashboard-page .analytics-greeting-sub { font-size: 10px !important; }
                }
                @media (max-width: 480px) {
                    .analytics-dashboard-page .analytics-greeting-title { font-size: 13px !important; }
                    .analytics-dashboard-page .analytics-greeting-sub { font-size: 9.5px !important; }
                }
                .analytics-dashboard-page .analytics-greeting-actions { display: none; }
                @media (min-width: 992px) {
                    .analytics-dashboard-page .analytics-header {
                        flex-direction: row;
                        align-items: center;
                        justify-content: space-between;
                        gap: 16px;
                    }
                    .analytics-dashboard-page .analytics-greeting { flex: 0 0 auto; }
                    .analytics-dashboard-page .filters-row { flex: 1 1 auto; min-width: 0; width: auto; justify-content: flex-end; }
                }

                /* Filter row + modern controls (matches homepage) */
                .analytics-dashboard-page .filters-row {
                    display: flex !important;
                    flex-wrap: nowrap !important;
                    align-items: center;
                    gap: 8px;
                    overflow: visible;
                    padding: 4px 2px 6px;
                    width: 100%;
                }
                .analytics-dashboard-page .filters-row > * { flex-shrink: 0 !important; }
                .analytics-dashboard-page .filters-to { white-space: nowrap; color: #94a3b8; padding: 0 2px; }
                @media (min-width: 768px) { .analytics-dashboard-page .filters-row { gap: 12px; } }

                /* Custom dropdown (replaces native <select> so the open list can be styled) */
                .analytics-dashboard-page .modern-dd {
                    position: relative;
                    display: inline-block;
                }
                .analytics-dashboard-page .modern-dd-trigger {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0 12px;
                    background-color: #fff;
                    color: #0f172a;
                    font-size: 12px;
                    font-weight: 500;
                    min-width: 170px;
                    height: 34px;
                    cursor: pointer;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }
                .analytics-dashboard-page .modern-dd-trigger:hover { border-color: #cbd5e1; }
                .analytics-dashboard-page .modern-dd-trigger:focus,
                .analytics-dashboard-page .modern-dd-trigger.is-open {
                    border-color: #008695;
                    box-shadow: 0 0 0 3px rgba(0,134,149,0.15);
                    outline: none;
                }
                .analytics-dashboard-page .modern-dd-lead { color: #008695; flex-shrink: 0; }
                .analytics-dashboard-page .modern-dd-label { flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .analytics-dashboard-page .modern-dd-caret { transition: transform 0.18s ease; flex-shrink: 0; }
                .analytics-dashboard-page .modern-dd-caret.is-open { transform: rotate(180deg); }

                .analytics-dashboard-page .modern-dd-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    z-index: 1100;
                    min-width: 100%;
                    width: max-content;
                    max-height: 320px;
                    overflow-y: auto;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    box-shadow: 0 10px 30px rgba(15,23,42,0.12), 0 2px 6px rgba(15,23,42,0.06);
                    padding: 6px;
                    animation: analyticsDdIn 0.14s ease-out;
                }
                @keyframes analyticsDdIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .analytics-dashboard-page .modern-dd-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    width: 100%;
                    background: transparent;
                    border: none;
                    border-radius: 6px;
                    padding: 8px 12px;
                    font-size: 12px;
                    font-weight: 500;
                    color: #0f172a;
                    text-align: left;
                    cursor: pointer;
                    transition: background 0.12s ease, color 0.12s ease;
                }
                .analytics-dashboard-page .modern-dd-item:hover { background: #f1f5f9; }
                .analytics-dashboard-page .modern-dd-item.is-active {
                    background: rgba(0,134,149,0.08);
                    color: #008695;
                    font-weight: 600;
                }
                .analytics-dashboard-page .modern-dd-item-muted { color: #64748b; }
                .analytics-dashboard-page .modern-dd-divider {
                    height: 1px;
                    background: #eef2f7;
                    margin: 6px 4px;
                }

                /* Overlay a dd/mm/yy formatted label on top of the native input */
                .analytics-dashboard-page .modern-date-wrap {
                    position: relative;
                    display: inline-block;
                }
                .analytics-dashboard-page .modern-date-display {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 500;
                    color: #0f172a;
                    letter-spacing: 0.01em;
                    pointer-events: none;
                    padding: 0 8px;
                    white-space: nowrap;
                }
                .analytics-dashboard-page .modern-date-wrap .modern-date {
                    color: transparent !important;
                    caret-color: transparent;
                }

                .analytics-dashboard-page .modern-date {
                    position: relative;
                    display: inline-block !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 8px !important;
                    padding: 5px 8px !important;
                    width: auto !important;
                    min-width: 115px !important;
                    height: 34px !important;
                    background-color: #fff !important;
                    color: #0f172a !important;
                    font-size: 12px !important;
                    font-weight: 500 !important;
                    text-align: center !important;
                    margin: 0 !important;
                    font-family: inherit;
                    cursor: pointer !important;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }
                .analytics-dashboard-page .modern-date:hover { border-color: #cbd5e1 !important; }
                .analytics-dashboard-page .modern-date:focus {
                    border-color: #008695 !important;
                    box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
                    outline: none;
                }
                .analytics-dashboard-page .modern-date::-webkit-calendar-picker-indicator {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    padding: 0;
                    margin: 0;
                    background: transparent;
                    color: transparent;
                    opacity: 0;
                    cursor: pointer;
                }
                .analytics-dashboard-page .modern-date::-webkit-inner-spin-button,
                .analytics-dashboard-page .modern-date::-webkit-clear-button { display: none; }
                @media (max-width: 576px) {
                    .analytics-dashboard-page .modern-date { padding: 5px 10px !important; }
                    .analytics-dashboard-page .modern-dd-trigger { min-width: 150px; padding: 0 10px; }
                }

                .analytics-dashboard-page .modern-refresh {
                    border: 1px solid #e2e8f0 !important;
                    background: #fff !important;
                    color: #475569 !important;
                    border-radius: 8px !important;
                    padding: 6px 14px !important;
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    height: 34px !important;
                    white-space: nowrap;
                    transition: all 0.15s ease;
                }
                .analytics-dashboard-page .modern-refresh:hover:not(:disabled) {
                    border-color: #008695 !important;
                    color: #008695 !important;
                    background: rgba(0,134,149,0.06) !important;
                }
                .analytics-dashboard-page .modern-refresh:disabled { opacity: 0.6; cursor: not-allowed; }
                .analytics-dashboard-page .filters-clear {
                    height: 34px !important;
                    border-radius: 8px !important;
                    font-size: 12px !important;
                    white-space: nowrap;
                }

                /* Mobile: hide desktop filters-row, show inline greeting icons */
                @media (max-width: 767.98px) {
                    .analytics-dashboard-page .filters-row { display: none !important; }
                    .analytics-dashboard-page .analytics-greeting-actions {
                        display: inline-flex;
                        gap: 6px;
                        flex-shrink: 0;
                    }
                    .analytics-dashboard-page .dgi-btn {
                        width: 34px; height: 34px;
                        display: inline-flex; align-items: center; justify-content: center;
                        border: 1px solid #e2e8f0;
                        background: #fff;
                        color: #475569;
                        border-radius: 8px;
                        cursor: pointer;
                        transition: all 0.15s ease;
                        padding: 0;
                    }
                    .analytics-dashboard-page .dgi-btn:hover:not(:disabled) {
                        border-color: #008695;
                        color: #008695;
                        background: rgba(0,134,149,0.06);
                    }
                    .analytics-dashboard-page .dgi-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                }

                /* ===== Date range bottom sheet ===== */
                @keyframes analyticsDdsSlideUp {
                    from { transform: translateY(100%); }
                    to   { transform: translateY(0); }
                }
                @keyframes analyticsDdsFadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                .dds-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(15,23,42,0.35);
                    z-index: 1099;
                    animation: analyticsDdsFadeIn 0.18s ease-out;
                }
                .dds-sheet {
                    position: fixed;
                    left: 0; right: 0; bottom: 0;
                    background: #fff;
                    border-radius: 18px 18px 0 0;
                    box-shadow: 0 -8px 24px rgba(15,23,42,0.15);
                    z-index: 1100;
                    display: flex;
                    flex-direction: column;
                    max-height: 88vh;
                    animation: analyticsDdsSlideUp 0.24s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .dds-handle {
                    width: 42px; height: 4px;
                    background: #cbd5e1;
                    border-radius: 999px;
                    margin: 8px auto 0;
                }
                .dds-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 10px 16px 8px;
                }
                .dds-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.01em;
                }
                .dds-close {
                    width: 28px; height: 28px;
                    border-radius: 6px;
                    border: 1px solid #e2e8f0;
                    background: #fff;
                    color: #475569;
                    display: inline-flex; align-items: center; justify-content: center;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .dds-close:hover { color: #dc2626; border-color: #fecaca; background: #fef2f2; }
                .dds-body {
                    padding: 4px 16px 8px;
                    overflow-y: auto;
                }
                .dds-presets {
                    display: flex;
                    flex-wrap: nowrap;
                    gap: 6px;
                    padding: 4px 0 12px;
                    border-bottom: 1px solid #eef2f7;
                    margin-bottom: 10px;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                    scroll-snap-type: x proximity;
                }
                .dds-presets::-webkit-scrollbar { display: none; }
                .dds-preset {
                    padding: 6px 12px;
                    font-size: 11px;
                    font-weight: 600;
                    color: #334155;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    border-radius: 999px;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    flex-shrink: 0;
                    white-space: nowrap;
                    scroll-snap-align: start;
                }
                .dds-preset:hover {
                    background: #fff;
                    border-color: #008695;
                    color: #008695;
                }
                .dds-field { margin-bottom: 10px; }
                .dds-label {
                    display: block;
                    font-size: 10px;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    margin-bottom: 4px;
                }
                .dds-input {
                    width: 100%;
                    height: 40px;
                    font-size: 13px;
                    padding: 6px 12px;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    color: #0f172a;
                    font-family: inherit;
                }
                .dds-input:focus {
                    outline: none;
                    border-color: #008695;
                    box-shadow: 0 0 0 3px rgba(0,134,149,0.15);
                }
                .dds-footer {
                    display: flex;
                    gap: 10px;
                    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
                    border-top: 1px solid #eef2f7;
                    background: #fff;
                }
                .dds-btn {
                    flex: 1;
                    height: 42px;
                    font-size: 13px;
                    font-weight: 700;
                    border-radius: 8px;
                    border: none;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .dds-btn-secondary {
                    background: #fff;
                    color: #0f172a;
                    border: 1px solid #e2e8f0;
                }
                .dds-btn-secondary:hover { border-color: #cbd5e1; background: #f8fafc; }
                .dds-btn-primary {
                    background: #008695;
                    color: #fff;
                }
                .dds-btn-primary:hover { background: #006672; }

                /* Quick-access section: grid on desktop, horizontal scroll on mobile */
                .analytics-dashboard-page .analytics-quick-access {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 12px;
                }
                @media (max-width: 991.98px) {
                    .analytics-dashboard-page .analytics-quick-access {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }
                @media (max-width: 575.98px) {
                    .analytics-dashboard-page .analytics-quick-access {
                        display: flex;
                        overflow-x: auto;
                        gap: 10px;
                        padding-bottom: 4px;
                        scrollbar-width: none;
                        scroll-snap-type: x proximity;
                    }
                    .analytics-dashboard-page .analytics-quick-access::-webkit-scrollbar { display: none; }
                    .analytics-dashboard-page .analytics-quick-access > * {
                        flex: 0 0 200px;
                        min-width: 200px;
                        scroll-snap-align: start;
                    }
                }

                /* Mobile: trim horizontal gutters like homepage */
                @media (max-width: 767.98px) {
                    body:has(.analytics-dashboard-page) .page-content {
                        padding-left: 8px !important;
                        padding-right: 8px !important;
                    }
                    body:has(.analytics-dashboard-page) .container-fluid,
                    body:has(.analytics-dashboard-page) .container {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    .analytics-dashboard-page .modern-panel-header { padding: 12px 12px 6px !important; }
                    .analytics-dashboard-page .modern-panel-body { padding: 6px 10px 12px !important; }
                    .analytics-dashboard-page .top-card { min-width: 220px; }
                    .analytics-dashboard-page .recharts-responsive-container { min-height: 220px; }
                }
            `}</style>
            {/* Header — uses shared .page-header-sticky (same as homepage) */}
            <div className="page-header-sticky analytics-header">
                <div className="analytics-greeting">
                    <div className="analytics-greeting-row">
                        <div className="analytics-greeting-text">
                            <h3 className="analytics-greeting-title mb-1 fw-bold">
                                Analytics Dashboard
                            </h3>
                            <p className="analytics-greeting-sub text-muted mb-0">
                                Tap a card to explore details
                            </p>
                        </div>
                        <div className="analytics-greeting-actions">
                            <button
                                type="button"
                                className="dgi-btn"
                                onClick={openDateSheet}
                                aria-label="Date range"
                            >
                                <FiCalendar size={15} />
                            </button>
                            <button
                                type="button"
                                className="dgi-btn"
                                onClick={() => fetchAnalytics(true)}
                                disabled={refreshing}
                                aria-label="Refresh"
                            >
                                <FiRefreshCw size={15} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="filters-row">
                    {(() => {
                        const activePreset = HOMEPAGE_PRESETS.find((p) => {
                            const { start, end } = computePresetRange(p);
                            return start === filters.startDate && end === filters.endDate;
                        });
                        const triggerLabel = activePreset ? activePreset.label : 'Custom Range';
                        return (
                            <div className="modern-dd filters-preset" ref={presetWrapRef}>
                                <button
                                    type="button"
                                    className={`modern-dd-trigger${presetOpen ? ' is-open' : ''}`}
                                    aria-haspopup="listbox"
                                    aria-expanded={presetOpen}
                                    onClick={() => setPresetOpen((v) => !v)}
                                >
                                    <FiCalendar size={13} className="modern-dd-lead" />
                                    <span className="modern-dd-label">{triggerLabel}</span>
                                    <svg
                                        className={`modern-dd-caret${presetOpen ? ' is-open' : ''}`}
                                        width="12" height="12" viewBox="0 0 24 24" fill="none"
                                        stroke="#008695" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                    >
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </button>
                                {presetOpen && (
                                    <div className="modern-dd-menu" role="listbox">
                                        {HOMEPAGE_PRESETS.map((p) => {
                                            const isActive = activePreset && activePreset.key === p.key;
                                            return (
                                                <button
                                                    key={p.key}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={isActive}
                                                    className={`modern-dd-item${isActive ? ' is-active' : ''}`}
                                                    onClick={() => {
                                                        const { start, end } = computePresetRange(p);
                                                        setFilters({ startDate: start, endDate: end });
                                                        setPresetOpen(false);
                                                    }}
                                                >
                                                    <span>{p.label}</span>
                                                    {isActive && (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#008695" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    )}
                                                </button>
                                            );
                                        })}
                                        <div className="modern-dd-divider" />
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={!activePreset}
                                            className={`modern-dd-item modern-dd-item-muted${!activePreset ? ' is-active' : ''}`}
                                            onClick={() => setPresetOpen(false)}
                                        >
                                            <span>Custom Range</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    <span className="modern-date-wrap">
                        <input
                            type="date"
                            className="modern-date"
                            style={{ fontSize: '12px' }}
                            value={filters.startDate}
                            onClick={(e) => { try { e.currentTarget.showPicker && e.currentTarget.showPicker(); } catch (_) {} }}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            max={filters.endDate || getTodayDate()}
                        />
                        <span className="modern-date-display" aria-hidden="true">{formatDdMmYy(filters.startDate) || 'Start date'}</span>
                    </span>
                    <span className="text-muted small filters-to">to</span>
                    <span className="modern-date-wrap">
                        <input
                            type="date"
                            className="modern-date"
                            style={{ fontSize: '12px' }}
                            value={filters.endDate}
                            onClick={(e) => { try { e.currentTarget.showPicker && e.currentTarget.showPicker(); } catch (_) {} }}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            min={filters.startDate}
                            max={getTodayDate()}
                        />
                        <span className="modern-date-display" aria-hidden="true">{formatDdMmYy(filters.endDate) || 'End date'}</span>
                    </span>
                    {isFilterActive() && (
                        <button
                            onClick={handleClearFilters}
                            className="btn btn-sm btn-outline-secondary filters-clear"
                            style={{ fontSize: '12px' }}
                            disabled={refreshing}
                        >
                            Clear
                        </button>
                    )}
                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn modern-refresh d-flex align-items-center gap-2"
                        style={{ fontSize: '12px' }}
                        disabled={refreshing}
                    >
                        <FiRefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                        <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                    </button>
                </div>
            </div>

            {showDateSheet && (
                <>
                    <div
                        className="dds-backdrop"
                        onClick={() => setShowDateSheet(false)}
                        aria-hidden="true"
                    />
                    <div className="dds-sheet" role="dialog" aria-label="Select date range">
                        <div className="dds-handle" aria-hidden="true" />
                        <div className="dds-header">
                            <span className="dds-title">Select date range</span>
                            <button
                                type="button"
                                className="dds-close"
                                onClick={() => setShowDateSheet(false)}
                                aria-label="Close"
                            >
                                <FiX size={16} />
                            </button>
                        </div>

                        <div className="dds-body">
                            <div className="dds-presets">
                                {HOMEPAGE_PRESETS.map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        className="dds-preset"
                                        onClick={() => {
                                            const { start, end } = computePresetRange(p);
                                            setSheetStart(start);
                                            setSheetEnd(end);
                                        }}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            <div className="dds-field">
                                <label className="dds-label">Start Date</label>
                                <input
                                    type="date"
                                    className="dds-input"
                                    value={sheetStart}
                                    max={sheetEnd || undefined}
                                    onChange={(e) => setSheetStart(e.target.value)}
                                />
                            </div>
                            <div className="dds-field">
                                <label className="dds-label">End Date</label>
                                <input
                                    type="date"
                                    className="dds-input"
                                    value={sheetEnd}
                                    min={sheetStart || undefined}
                                    onChange={(e) => setSheetEnd(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="dds-footer">
                            <button
                                type="button"
                                className="dds-btn dds-btn-secondary"
                                onClick={() => setShowDateSheet(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="dds-btn dds-btn-primary"
                                onClick={applyDateSheet}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Quick access navigation — grid on desktop, horizontal scroll on mobile */}
            <div className="analytics-section analytics-quick-access">
                {[
                    {
                        title: "Search Analytics",
                        sub: "Query volume & intent",
                        icon: <FiSearch size={16} />,
                        iconColor: "#3b82f6",
                        iconBg: "#eff6ff",
                        to: "/search-analytics",
                    },
                    {
                        title: "Location Analytics",
                        sub: "Geographic reach & hotspots",
                        icon: <FiMapPin size={16} />,
                        iconColor: "#10b981",
                        iconBg: "#ecfdf5",
                        to: "/location-analytics",
                    },
                    {
                        title: "Promo Analytics",
                        sub: "Campaign performance",
                        icon: <FiGift size={16} />,
                        iconColor: "#ef4444",
                        iconBg: "#fef2f2",
                        to: "/promo-analytics",
                    },
                    {
                        title: "AI Listing",
                        sub: "AI-generated listings",
                        icon: <FaWandMagicSparkles size={14} />,
                        iconColor: "#8b5cf6",
                        iconBg: "#f5f3ff",
                        to: "/ai-listing-analytics",
                        badge: "NEW",
                    },
                ].map((card, i) => (
                    <div
                        key={i}
                        onClick={() => navigate(card.to)}
                        className="qa-card"
                    >
                        <div className="qa-icon" style={{ background: card.iconBg, color: card.iconColor }}>
                            {card.icon}
                        </div>
                        <div className="flex-grow-1 min-w-0">
                            <div className="d-flex align-items-center gap-2">
                                <h6 className="qa-title text-truncate">{card.title}</h6>
                                {card.badge && <span className="badge qa-badge">{card.badge}</span>}
                            </div>
                            <p className="qa-sub text-truncate">{card.sub}</p>
                        </div>
                        <FiChevronRight size={14} className="text-muted flex-shrink-0" />
                    </div>
                ))}
            </div>

            {isLoadingInitial ? (
                <div className="modern-panel analytics-section">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 320 }}>
                        <Loader />
                    </div>
                </div>
            ) : hasNoData ? (
                <div className="modern-panel analytics-section">
                    <div className="analytics-empty">
                        <div className="analytics-empty-icon" aria-hidden="true">📊</div>
                        <div className="analytics-empty-title">No activity data available</div>
                        <div className="analytics-empty-sub">Try adjusting the date range or check back later</div>
                    </div>
                </div>
            ) : (
              <>
            {/* Activity Overview */}
            <div className="analytics-section modern-panel">
                <div className="modern-panel-header">
                    <div>
                        <h5 className="panel-title">Activity Overview</h5>
                        <p className="panel-subtle">Time spent, events, and unique users per activity</p>
                    </div>
                </div>
                <div className="modern-panel-body">
                    <div className="h-card-strip">
                        {[
                            { title: "Store",    icon: <FaStore />,        type: "store_view",    color: "#6366F1", bg: "rgba(99,102,241,0.10)",  navigateTo: "/store-analytics" },
                            { title: "Cart",     icon: <FiShoppingCart />, type: "cart_add",      color: "#F97316", bg: "rgba(249,115,22,0.10)",  navigateTo: "/cart-analytics" },
                            { title: "Purchase", icon: <FiDollarSign />,   type: "purchase",      color: "#22C55E", bg: "rgba(34,197,94,0.10)",   navigateTo: "/purchase-analytics" },
                            { title: "Brand",    icon: <FiTag />,          type: "brand_view",    color: "#F43F5E", bg: "rgba(244,63,94,0.10)",   navigateTo: "/brand-analytics" },
                            { title: "Category", icon: <FiGrid />,         type: "category_view", color: "#06B6D4", bg: "rgba(6,182,212,0.10)",   navigateTo: "/category-analytics" },
                            { title: "Product",  icon: <FiPackage />,      type: "product_view",  color: "#8B5CF6", bg: "rgba(139,92,246,0.10)",  navigateTo: "/product-analytics" },
                        ].map((card, i) => {
                            const activityData = getActivityData(card.type);
                            const handleCardClick = () => {
                                if (card.navigateTo === '/search-analytics' || card.navigateTo === '/location-analytics') {
                                    navigate(card.navigateTo);
                                } else {
                                    toast.error(`${card.title} analytics page is coming soon!`);
                                }
                            };
                            return (
                                <div
                                    key={i}
                                    className="modern-stat-card"
                                    style={{ minWidth: 210 }}
                                    onClick={handleCardClick}
                                >
                                    <div className="stat-top-row">
                                        <div className="stat-label">{card.title}</div>
                                        <div className="stat-icon-wrap" style={{ background: card.bg, color: card.color }}>
                                            {React.cloneElement(card.icon, { size: 16 })}
                                        </div>
                                    </div>
                                    <h4 className="stat-value">
                                        {activityData?.totalTimeSpentMinutes ? formatTimeSpent(activityData.totalTimeSpentMinutes) : "0 min"}
                                    </h4>
                                    <div className="stat-foot">
                                        <span className="stat-foot-item">
                                            <FiActivity size={11} /> {activityData?.count || 0} events
                                        </span>
                                        <span className="stat-foot-item">
                                            <FiUsers size={11} /> {activityData?.uniqueUsers || 0} users
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Activity Trends Chart */}
            <div className="analytics-section modern-panel">
                <div className="modern-panel-header">
                    <div>
                        <h5 className="panel-title">Activity Trends Over Time</h5>
                        <p className="panel-subtle">Events and unique users across the selected range</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <span className="d-inline-flex align-items-center gap-1" style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#008695' }} />
                            Total Events
                        </span>
                        <span className="d-inline-flex align-items-center gap-1" style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8b5cf6' }} />
                            Unique Users
                        </span>
                    </div>
                </div>
                <div className="modern-panel-body">
                    <div style={{ height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={analytics?.activityTrends || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    axisLine={{ stroke: '#e2e8f0' }}
                                    tickLine={false}
                                />
                                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    formatter={(value, name) => {
                                        if (name === 'totalEvents') return [value, 'Total Events'];
                                        if (name === 'uniqueUsers') return [value, 'Unique Users'];
                                        return [value, name];
                                    }}
                                    labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '10px',
                                        boxShadow: '0 10px 30px rgba(15,23,42,0.10)',
                                        padding: '10px 14px',
                                        fontSize: '12px',
                                    }}
                                    itemStyle={{ padding: '2px 0' }}
                                    labelStyle={{ fontWeight: 700, marginBottom: 6, color: '#0f172a', fontSize: 11 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="totalEvents"
                                    name="Total Events"
                                    stroke="#008695"
                                    strokeWidth={2.5}
                                    dot={{ strokeWidth: 2, r: 3, fill: '#008695', stroke: '#fff' }}
                                    activeDot={{ r: 5, fill: '#008695', stroke: '#fff', strokeWidth: 2 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="uniqueUsers"
                                    name="Unique Users"
                                    stroke="#8b5cf6"
                                    strokeWidth={2.5}
                                    dot={{ strokeWidth: 2, r: 3, fill: '#8b5cf6', stroke: '#fff' }}
                                    activeDot={{ r: 5, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Top Performing Sections — shared card */}
            {[
                { key: 'topStores',     title: 'Top Performing Stores',     unit: 'stores',     fallback: <FaStore size={24} />,   items: analytics?.topStores },
                { key: 'topBrands',     title: 'Top Performing Brands',     unit: 'brands',     fallback: <FiTag size={24} />,     items: analytics?.topBrands },
                { key: 'topCategories', title: 'Top Performing Categories', unit: 'categories', fallback: <FiGrid size={24} />,    items: analytics?.topCategories },
                { key: 'topProducts',   title: 'Top Performing Products',   unit: 'products',   fallback: <FiPackage size={24} />, items: analytics?.topProducts },
            ].filter(s => s.items && s.items.length > 0).map((section) => (
                <div key={section.key} className="analytics-section modern-panel">
                    <div className="modern-panel-header">
                        <div>
                            <h5 className="panel-title">{section.title}</h5>
                            <p className="panel-subtle">Ranked by total time spent</p>
                        </div>
                        <span className="panel-count">{section.items.length} {section.unit}</span>
                    </div>
                    <div className="modern-panel-body">
                        <div className="h-card-strip">
                            {section.items.map((item, index) => (
                                <div key={item.id || index} className="top-card">
                                    <div className="top-card-img-wrap">
                                        <span className="top-card-rank">#{index + 1}</span>
                                        {item.image ? (
                                            <img src={item.image} alt={item.name} className="top-card-img" />
                                        ) : (
                                            <div className="top-card-fallback">{section.fallback}</div>
                                        )}
                                    </div>
                                    <div className="flex-grow-1 min-w-0">
                                        <h6 className="top-card-name">{item.name}</h6>
                                        <h4 className="top-card-value">
                                            {item.timeSpentMinutes ? formatTimeSpent(item.timeSpentMinutes) : 'N/A'}
                                        </h4>
                                        <div className="top-card-meta">
                                            <span className="top-card-meta-item">
                                                <FiEye size={11} /> {item.views || 0} events
                                            </span>
                                            <span className="top-card-meta-item">
                                                <FiUsers size={11} /> {item.uniqueUsers || 0} users
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
              </>
            )}



        </div>
    );
};

export default AnalyticsDashboard;
